import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";

/**
 * DR -> RE auto-production (owner t804u), owner-only. Two entry points share this file:
 *  - (the Deep Research tab's "Auto-produce top 3 (RE)" lives in deep-research/DeepResearchAutoProduce.tsx: FIX-062 isolation)
 *  - ReAutoReButton: RE entry screen, "Auto-produce (both personas)" for the selected source video, with the lead persona chosen by the owner (no default).
 * Safety net (O2): the button locks on click, a confirm dialog shows what will be produced and the estimate, one run at a time, max 3 button runs a day,
 * a produced source can never run again (§15). The run card shows progress and has Stop.
 */
interface RePreview { readonly produced: { readonly message: string } | null; readonly tooLong: boolean; readonly videos: number; readonly estimate: { readonly text: string } }
interface Status { readonly enabled: boolean; readonly activeRun: { readonly id: string } | null; readonly buttonRuns: { readonly used: number; readonly max: number }; readonly settings: { readonly paused: boolean; readonly pausedReason: string | null }; readonly personas: { readonly EMMA: string; readonly YUNA_1: string } }
interface RunView { readonly run: { readonly id: string; readonly status: string; readonly error: string | null; readonly targetVideos: number }; readonly report: { readonly line?: string } & Record<string, unknown> }

const bodyMessage = (e: unknown, fallback: string): string => {
  const b = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
  return b?.message ?? b?.error ?? fallback;
};

function useReAutoStatus(tenantId: string): { status: Status | null; hidden: boolean; reload: () => void } {
  const [status, setStatus] = useState<Status | null>(null);
  const [hidden, setHidden] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api.get<Status>(`/re/${tenantId}/auto/status`).then((s) => { if (live) setStatus(s); }, (e: unknown) => { if (live && e instanceof ApiError && (e.status === 403 || e.status === 503 || e.status === 404)) setHidden(true); });
    return () => { live = false; };
  }, [tenantId, tick]);
  return { status, hidden, reload: () => setTick((x) => x + 1) };
}

export function ReAutoRunCard({ tenantId, runId, csrfToken, onEnd }: { readonly tenantId: string; readonly runId: string; readonly csrfToken: string | null; readonly onEnd?: () => void }) {
  const [view, setView] = useState<RunView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const ended = useRef(false);
  useEffect(() => {
    let live = true;
    const load = (): void => {
      api.get<RunView>(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}`).then((v) => {
        if (!live) return; setView(v);
        if (v.run.status !== "running" && !ended.current) { ended.current = true; onEnd?.(); }
      }).catch(() => undefined);
    };
    load();
    const t = setInterval(load, 15_000);
    return () => { live = false; clearInterval(t); };
  }, [tenantId, runId]);
  const stop = async (): Promise<void> => {
    if (!window.confirm("Stop this run? No new step or paid call starts; videos already submitted stay in the normal pipeline.")) return;
    try { await api.post(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}/stop`, csrfToken, {}); setMsg("Stopped."); } catch (e) { setMsg(bodyMessage(e, "Stop failed.")); }
  };
  return (
    <div className="card" data-testid="re-auto-run">
      <strong>Auto-production run</strong> <span className="muted">{runId}</span>
      <p data-testid="re-auto-run-status">Status: {view?.run.status ?? "…"}{view?.run.error ? ` (${view.run.error})` : ""}</p>
      {view?.report.line ? <p data-testid="re-auto-run-summary">{String(view.report.line)}</p> : null}
      {view?.run.status === "running" ? <button type="button" data-testid="re-auto-stop" onClick={() => void stop()}>Stop</button> : null}
      {msg ? <p className="muted">{msg}</p> : null}
    </div>
  );
}

/** FIX-079 (owner t1528u): owner-only Unpause on a paused card. EXISTING endpoint POST /re/:t/auto/pause {paused:false}, owner session + CSRF; confirm first; refresh after. */
export function ReAutoUnpause({ tenantId, csrfToken, status, onDone }: { readonly tenantId: string; readonly csrfToken: string | null; readonly status: Status; readonly onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (!status.enabled || !status.settings.paused) return null;
  const resume = async (): Promise<void> => {
    const lines = [`Unpause RE auto-production?${status.settings.pausedReason ? `\nPaused because: ${status.settings.pausedReason}` : ""}`];
    if (status.activeRun) lines.push(`Warning: run ${status.activeRun.id} is still running - its paid steps will continue once unpaused.`);
    lines.push("If the cause (e.g. WaveSpeed credit) is not fixed, it may pause again.");
    if (!window.confirm(lines.join("\n"))) return;
    setBusy(true); setMsg(null);
    try { await api.post(`/re/${tenantId}/auto/pause`, csrfToken, { paused: false }); setMsg("Unpaused."); onDone(); }
    catch (e) { setMsg(bodyMessage(e, "Could not unpause.")); }
    finally { setBusy(false); }
  };
  return (
    <span data-testid="re-auto-unpause-wrap">
      {" "}<button type="button" data-testid="re-auto-unpause" disabled={busy} onClick={() => void resume()}>Unpause</button>
      {msg ? <span className="muted" data-testid="re-auto-unpause-msg"> {msg}</span> : null}
    </span>
  );
}

export function ReAutoReButton({ tenantId, sourceVideoId, csrfToken }: { readonly tenantId: string; readonly sourceVideoId: string | null; readonly csrfToken: string | null }) {
  const { status, hidden, reload } = useReAutoStatus(tenantId);
  const [pv, setPv] = useState<RePreview | null>(null);
  const [lead, setLead] = useState<"" | "EMMA" | "YUNA_1">("");
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setPv(null); setLead("");
    if (!sourceVideoId || hidden) return;
    let live = true;
    api.get<RePreview>(`/re/${tenantId}/auto/preview?sourceVideoId=${encodeURIComponent(sourceVideoId)}`).then((p) => { if (live) setPv(p); }, () => undefined);
    return () => { live = false; };
  }, [tenantId, sourceVideoId, hidden]);
  if (hidden || status === null || !sourceVideoId) return null;
  const active = runId ?? status.activeRun?.id ?? null;
  const off = pv?.produced ? pv.produced.message : pv?.tooLong ? "Only sources of 30 seconds or less can be produced." : !status.enabled ? "Auto-production is switched off." : status.settings.paused ? `Auto-production is paused.${status.settings.pausedReason ? ` Reason: ${status.settings.pausedReason}` : ""}` : status.buttonRuns.used >= status.buttonRuns.max ? `The buttons ran ${status.buttonRuns.used} times today (max ${status.buttonRuns.max}).` : null;
  const go = async (): Promise<void> => {
    if (!lead || !pv) return;
    setBusy(true); setMsg(null);
    try {
      const other = lead === "EMMA" ? "YUNA_1" : "EMMA";
      if (!window.confirm(`Produce ${pv.videos} videos from this source: ${lead} leads (3 options), ${other} copies the same 3. Estimated ${pv.estimate.text}. All approvals are automatic.`)) return;
      const out = await api.post<{ run: { id: string } }>(`/re/${tenantId}/auto/runs`, csrfToken, { trigger: "button_re", sourceVideoId, leadPersonaId: status.personas[lead] });
      setRunId(out.run.id);
    } catch (e) { setMsg(bodyMessage(e, "Could not start the run.")); }
    finally { setBusy(false); reload(); }
  };
  return (
    <div className="card" data-testid="re-auto-re">
      <strong>Auto-produce (both personas)</strong>
      <p>Lead persona:{" "}
        <label><input type="radio" name="re-auto-lead" data-testid="re-auto-lead-emma" checked={lead === "EMMA"} onChange={() => setLead("EMMA")} disabled={off !== null} /> EMMA</label>{" "}
        <label><input type="radio" name="re-auto-lead" data-testid="re-auto-lead-yuna" checked={lead === "YUNA_1"} onChange={() => setLead("YUNA_1")} disabled={off !== null} /> YUNA_1</label>
      </p>
      <button type="button" className="auto-produce" data-testid="re-auto-re-button" disabled={busy || !pv || !lead || active !== null || off !== null} title={off ?? undefined} onClick={() => void go()}>Auto-produce (both personas)</button>
      {off ? <p className="muted" data-testid="re-auto-re-off">{off}</p> : null}
      <ReAutoUnpause tenantId={tenantId} csrfToken={csrfToken} status={status} onDone={reload} />
      {msg ? <p className="error-banner" role="alert">{msg}</p> : null}
      {active ? <ReAutoRunCard tenantId={tenantId} runId={active} csrfToken={csrfToken} onEnd={reload} /> : null}
    </div>
  );
}
